import type {
  AgentByOldHandleAnswer,
  AgentHistoryFilter,
  AgentHistoryPage,
  AgentNowView,
  MemoryHistory,
} from "@minga/protocol"
import type { Effect } from "effect"
import type { ProjectClient } from "@/model/live/sync"

/**
 * Wave C (decision 0071, spec §8.4): the agent page's four reads over the project socket (`ProjectRpcs.AgentNow`,
 * `AgentHistory`, `MemoryHistory`, `AgentByOldHandle`). The Project DO builds every answer (core `agentOverview` and
 * friends); the web only renders them. Every member may read them, viewers too.
 */
export interface AgentPageApi {
  /** Now: Doing now, Tracking, Open loops, Since her last turn, the daily check's line. */
  readonly now: (agentId: string) => Promise<AgentNowView>
  /** History: her runs newest first, whole days per page; `before` = the previous page's `nextBefore`. */
  readonly history: (input: {
    readonly agentId: string
    readonly before?: number | undefined
    readonly filter?: AgentHistoryFilter | undefined
  }) => Promise<AgentHistoryPage>
  /** A memory's revisions, newest first. */
  readonly memoryHistory: (memoryId: string) => Promise<MemoryHistory>
  /** The agent whose earlier handle this was (a renamed handle's old link), or null. */
  readonly byOldHandle: (handle: string) => Promise<AgentByOldHandleAnswer>
}

/**
 * The four reads over one client. `call` waits for the socket and runs the client's effect (connection.ts passes its
 * `Deferred`-backed runner, as for the wave K reads).
 */
export const agentPageOf = (
  call: <A>(f: (client: ProjectClient) => Effect.Effect<A, unknown>) => Promise<A>,
): AgentPageApi => ({
  now: (agentId) => call((client) => client.AgentNow({ agentId: agentId as never })),
  history: ({ agentId, before, filter }) =>
    call((client) =>
      client.AgentHistory({
        agentId: agentId as never,
        ...(before !== undefined ? { before: before as never } : {}),
        ...(filter !== undefined ? { filter } : {}),
      }),
    ),
  memoryHistory: (memoryId) => call((client) => client.MemoryHistory({ memoryId: memoryId as never })),
  byOldHandle: (handle) => call((client) => client.AgentByOldHandle({ handle })),
})

/** Before the socket is up: every read rejects (the queries retry once connected). */
export const offlineAgentPage: AgentPageApi = {
  now: () => Promise.reject(new Error("The project is not connected yet.")),
  history: () => Promise.reject(new Error("The project is not connected yet.")),
  memoryHistory: () => Promise.reject(new Error("The project is not connected yet.")),
  byOldHandle: () => Promise.reject(new Error("The project is not connected yet.")),
}
